import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import Modal from 'react-modal';
import ReservationForm from './ReservationForm';
import AssignMeats from './AssignMeats';
import config from '../config';
import "./ReservationsList.css";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { parseToLocalDate, isTodayLocal, formatDateTimeGR, startOfToday } from './shared/dateUtils';
import { useIsMobile, useCardifyTable } from '../Responsive';
import WalkedInSitting from './WalkedInSitting';
import AutoAssignModal from './AutoAssignModal';
import { useTranslation } from "react-i18next";
import * as GR from "flatpickr/dist/l10n/gr.js";
import { toast } from "react-toastify";
import { enqueuePrintJob } from "../utils/printQueue";
import TableAssignment from "./TableAssignment";

import TableSelector from "./TableSelector";
import { createPortal } from "react-dom";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
} from "recharts";


import i18n from "../i18n";
const tr = i18n.t.bind(i18n);

// ===== Mini availability helpers (shared with ReservationForm-like behaviour) =====
const parseTimeMinutes = (arrival) => {
  if (!arrival) return null;

  if (typeof arrival === "string") {
    // "YYYY-MM-DDTHH:mm..."
    const h = parseInt(arrival.slice(11, 13), 10);
    const m = parseInt(arrival.slice(14, 16), 10);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
    return h * 60 + m;
  }

  const d = new Date(arrival);
  if (Number.isNaN(d.getTime())) return null;
  return d.getHours() * 60 + d.getMinutes();
};

const toHHMM = (mins) => {
  const m = Math.max(0, Math.min(24 * 60 - 1, Number(mins) || 0));
  const hh = String(Math.floor(m / 60)).padStart(2, "0");
  const mm = String(m % 60).padStart(2, "0");
  return `${hh}:${mm}`;
};

const buildSeatGraphData = (tableAvailability, reservationDurationMinutes = 120) => {
  if (!Array.isArray(tableAvailability) || tableAvailability.length === 0) {
    return { totalCapacity: 0, slots: [], peakReserved: 0 };
  }

  // Y-axis max: sum of all table capacities
  const totalCapacity = tableAvailability.reduce(
    (sum, table) => sum + (Number(table.capacity) || 0),
    0
  );

  // Collect unique reservations by id so we don't double-count people
  const reservationMap = new Map();

  tableAvailability.forEach((table) => {
    (table.reservations || []).forEach((res) => {
      if (!res || res.id == null) return;
      if (reservationMap.has(res.id)) return; // seen via another table

      let start = parseTimeMinutes(res.arrival);
      if (start == null) return;

      const WINDOW_START = 12 * 60;   // 12:00 in minutes from midnight
      const WINDOW_END   = 24 * 60;   // 24:00

      const rawEnd = start + (Number(reservationDurationMinutes) || 120);

      // Completely outside our 12-24 window -> skip
      if (rawEnd <= WINDOW_START || start >= WINDOW_END) return;

      // Clamp to the 12-24 window
      start = Math.max(start, WINDOW_START);
      const end = Math.min(rawEnd, WINDOW_END);

      reservationMap.set(res.id, {
        start,
        end,
        people: Number(res.people) || 0,
      });
    });
  });

  // 5-minute resolution
  const SLOT_MINUTES = 5;
  const DAY_START = 12 * 60;
  const SLOT_COUNT = (24 * 60 - DAY_START) / SLOT_MINUTES; // 144 slots

  const slots = [];
  let peakReserved = 0;

  for (let i = 0; i < SLOT_COUNT; i++) {
    const slotStart = DAY_START + i * SLOT_MINUTES;
    const slotEnd = slotStart + SLOT_MINUTES;

    let reservedSeats = 0;

    reservationMap.forEach((res) => {
      const overlaps = res.start < slotEnd && res.end > slotStart;
      if (overlaps) reservedSeats += res.people;
    });

    if (reservedSeats > totalCapacity) reservedSeats = totalCapacity;
    if (reservedSeats > peakReserved) peakReserved = reservedSeats;

    slots.push({ label: toHHMM(slotStart), value: reservedSeats });
  }

  return { totalCapacity, slots, peakReserved };
};

// ===== Mini capacity graph header helpers (same as ReservationForm) =====
const pct = (num, den, decimals = 0) => {
  const n = Number(num) || 0;
  const d = Number(den) || 0;
  if (!d) return 0;
  const v = (100 * n) / d;
  return decimals > 0 ? Number(v.toFixed(decimals)) : Math.round(v);
};

// % of the graph area "used/orange" = integral(value) / (capacity * slotsCount)
const getAreaUsedPercent = (graph) => {
  if (!graph?.slots?.length || !graph.totalCapacity) return 0;
  const sum = graph.slots.reduce((acc, s) => acc + (Number(s.value) || 0), 0);
  const den = graph.totalCapacity * graph.slots.length;
  return pct(sum, den, 1); // 1 decimal looks nice
};

const getMaxInfo = (graph) => {
  if (!graph?.slots?.length) return { time: "-", value: 0, percent: 0 };
  let best = graph.slots[0];
  for (const s of graph.slots) {
    if ((Number(s.value) || 0) > (Number(best.value) || 0)) best = s;
  }
  const v = Number(best.value) || 0;
  return {
    time: best.label || "-",
    value: v,
    percent: pct(v, graph.totalCapacity, 0),
  };
};

const MiniAvailabilityGraph = React.memo(function MiniAvailabilityGraph({
  dateKey,
  graph,
  activeLabel,
  onHover,
  onLeave,
}) {
  const wrapRef = React.useRef(null);

  const handlePick = (state) => {
    const label = state?.activeLabel;
    if (!label) return;

    const rect = wrapRef.current?.getBoundingClientRect?.();
    if (!rect) return;

    const reservedSeats = state?.activePayload?.[0]?.value;

    onHover?.({
      dateKey,
      label,
      anchorRect: rect,
      reservedSeats: Number(reservedSeats) || 0,
    });
  };

  return (
    <div
      ref={wrapRef}
      className="dgMiniGraph"
      onMouseLeave={onLeave}
    >
      {graph?.slots?.length ? (
        <ResponsiveContainer width="100%" height={24}>
          <AreaChart
            data={graph.slots}
            margin={{ top: 2, right: 0, left: 0, bottom: 0 }}
            onMouseMove={handlePick}
            onTouchStart={handlePick}
            onTouchMove={handlePick}
            onClick={handlePick}
          >
            <XAxis dataKey="label" hide />
            <YAxis hide domain={[0, Math.max(1, Number(graph.totalCapacity) || 1)]} />
            <Tooltip content={() => null} />
            {!!activeLabel && (
              <ReferenceLine x={activeLabel} stroke="rgba(0,0,0,0.45)" />
            )}
            <Area
              type="monotone"
              dataKey="value"
              stroke="var(--accent)"
              fill="var(--accent)"
              fillOpacity={0.42}
              isAnimationActive={false}
              activeDot={false}
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      ) : (
        <div className="dgMiniGraphPlaceholder" />
      )}
    </div>
  );
});

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

function computeMiniAvailTooltipLayout({ graphRect, baseW, baseH, headH = 48 }) {
  const vh = window.innerHeight;
  const PAD = 8;      // viewport margin
  const GAP = 6;      // gap between tooltip and graph

  // total overhead per side ~= popover padding (10) + border (1) = 11
  const CHROME = 11;

  // usable rectangle: LEFT OF GRAPH, full viewport height
  const maxOuterW = Math.max(140, Math.floor(graphRect.left - PAD - GAP));
  const maxOuterH = Math.max(140, Math.floor(vh - PAD * 2));

  const maxContentW = Math.max(80, maxOuterW - CHROME * 2);
  const maxContentH = Math.max(80, maxOuterH - headH - CHROME * 2);

  // maximize scale (no artificial cap)
  const rawS = Math.min(maxContentW / baseW, maxContentH / baseH);
  const scale = Number.isFinite(rawS) ? Math.max(0.18, rawS) : 0.6;

  const contentW = Math.round(baseW * scale);
  const contentH = Math.round(baseH * scale);

  // tight-fit outer size (no dead space)
  const outerW = contentW + CHROME * 2;
  const outerH = contentH + headH + CHROME * 2;

  // right edge hugs graph's left edge
  const right = Math.floor(graphRect.left - GAP);
  const left = Math.floor(right - outerW);

  // allow shifting vertically to use max height
  const preferredTop = Math.floor(graphRect.top);
 const top = Math.floor(clamp(preferredTop, PAD, vh - PAD - outerH));

  return { left, top, outerW, outerH, scale };
}


const ReservationsList = () => {

  const [reservations, setReservations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMeatsModalOpen, setIsMeatsModalOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [assignedTables, setAssignedTables] = useState([]); // Holds assigned tables for a reservation
  const [states, setStates] = useState([]);
  const [filterMode, setFilterMode] = useState('all'); // 'all', 'today', 'future'
  const [filteredReservations, setFilteredReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const fpInstanceRef = useRef(null);
  const suppressFpChangeRef = useRef(false);
  const [refresh, setRefresh] = useState(false);
  // Shared per-reservation sticker print state (0 red / 1 yellow / 2 green / 3 orange)
  const [printStatusMap, setPrintStatusMap] = useState({});

  // History modal (past reservations + cancellations/no-shows by date)
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyDate, setHistoryDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [historyData, setHistoryData] = useState({ reservations: [], cancellations: [] });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [undoingNoShowId, setUndoingNoShowId] = useState(null);

  // Smart auto-assignment (hidden tool, admins only)
  const [autoAssignOpen, setAutoAssignOpen] = useState(false);
  const isAdmin = useMemo(() => {
    try { return Boolean(JSON.parse(localStorage.getItem('user') || '{}')?.view_admin); }
    catch { return false; }
  }, []);

  const fetchHistory = useCallback(async (date) => {
    setHistoryLoading(true);
    try {
      const { data } = await axios.get(
        tr('{{API_BASE_URL}}/reservations/history', { API_BASE_URL: config.API_BASE_URL }),
        { params: { date } }
      );
      setHistoryData({
        reservations: Array.isArray(data?.reservations) ? data.reservations : [],
        cancellations: Array.isArray(data?.cancellations) ? data.cancellations : [],
      });
    } catch (e) {
      console.error('Failed to load reservations history', e);
      toast.error(tr('Failed to load history.'));
    } finally {
      setHistoryLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (historyOpen) fetchHistory(historyDate);
  }, [historyOpen, historyDate, fetchHistory]);

  const handleUndoNoShow = async (cancellation) => {
    if (undoingNoShowId) return;
    setUndoingNoShowId(cancellation.id);
    try {
      await axios.post(
        tr('{{API_BASE_URL}}/reservations/{{id}}/undo-noshow', {
          API_BASE_URL: config.API_BASE_URL,
          id: cancellation.id,
        })
      );
      toast.success(tr('No-show undone — the reservation is back in the list.'), { autoClose: 3500 });
      await fetchHistory(historyDate);
      setRefresh((r) => !r); // refresh the main list so the reservation reappears
    } catch (e) {
      console.error('Undo no-show failed', e);
      toast.error(e?.response?.data?.error || tr('Failed to undo the no-show.'));
    } finally {
      setUndoingNoShowId(null);
    }
  };
  const [areasList, setAreasList] = useState([]);
  const [meatsList, setMeatsList] = useState([]);
  const [cooktypesList, setCooktypesList] = useState([]);
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const [qrUrl, setQrUrl] = useState("");
  const [qrForReservation, setQrForReservation] = useState(null);
  const [isFbModalOpen, setIsFbModalOpen] = useState(false);
  const [fbReservation, setFbReservation] = useState(null);
  const [qrLang, setQrLang] = useState("en");

  const [planId, setPlanId] = useState(null);
  const isStarterPlan = planId === 1;

  // Column metrics for aligning date strip under the Arrival column (desktop)
  
  // Collapsible day groups (dateKey -> true means collapsed)
  const [collapsedDays, setCollapsedDays] = useState({});

  // Mini availability (desktop/tablet date-group strip)
  const [reservationDurationMinutes, setReservationDurationMinutes] = useState(120);
  const [allTables, setAllTables] = useState(null); // /tables result (includes layout coords)
  const [miniSeatGraphByDate, setMiniSeatGraphByDate] = useState({}); // dateKey -> { totalCapacity, slots, peakReserved }
  const [miniTableCalendarByDate, setMiniTableCalendarByDate] = useState({}); // dateKey -> { byTableId: { [id]: reservations[] } }
  const [miniHover, setMiniHover] = useState(null); // { dateKey, label, anchorRect, reservedSeats? }
  const miniHoverCloseTimerRef = useRef(null);

  // Mini popover sizing helpers (measure real TableSelector footprint so we can scale up safely)
  const miniScaleRef = useRef(null);
  const miniHeadRef = useRef(null);
  const [miniBaseSize, setMiniBaseSize] = useState({ w: 1050, h: 720 });
  const [miniHeadH, setMiniHeadH] = useState(48);
  
  const [dayAnimating, setDayAnimating] = useState({}); // dateKey -> true while collapsing animation
  const [dayExpanding, setDayExpanding] = useState({}); // dateKey -> true while expanding animation

  const targetDayISO = useMemo(() => {
    // prefer the chosen date from Flatpickr; fallback to local today ISO
    if (selectedDate) return selectedDate;
    const d = startOfToday();
    return tr('{{y}}-{{m}}-{{dd}}', {
      y: d.getFullYear(),
      m: String(d.getMonth() + 1).padStart(2, '0'),
      dd: String(d.getDate()).padStart(2, '0')
    });
  }, [selectedDate]);

  const toggleDayGroup = useCallback((dateKey) => {
    if (!dateKey) return;

    setCollapsedDays((prev) => {
      const currentlyCollapsed = Boolean(prev[dateKey]);

      // Expand with a tiny fade/slide-in
      if (currentlyCollapsed) {
        setDayExpanding((a) => ({ ...a, [dateKey]: true }));
        window.setTimeout(() => {
          setDayExpanding((a) => {
            const { [dateKey]: _, ...rest } = a;
            return rest;
          });
        }, 180);
        return { ...prev, [dateKey]: false };
      }

      // Collapse with a tiny fade/slide first
      setDayAnimating((a) => ({ ...a, [dateKey]: true }));
      window.setTimeout(() => {
        setCollapsedDays((p2) => ({ ...p2, [dateKey]: true }));
        setDayAnimating((a) => {
          const { [dateKey]: _, ...rest } = a;
          return rest;
        });
      }, 180);

      // Keep expanded until the timeout collapses it
      return prev;
    });
  }, []);

  const GreekLocale = GR.Greek || GR.default?.Greek || GR.default;

  const onRefreshStable = useCallback(() => setRefresh(r => !r), []);

  // Poll the shared print status for the listed reservations (all devices see
  // the same red/yellow/green/orange sticker state).
  useEffect(() => {
    let cancelled = false;

    const fetchPrintStatuses = async () => {
      const ids = (reservations || []).map((r) => r?.id).filter(Boolean);
      if (!ids.length) return;
      try {
        const { data } = await axios.get(
          tr('{{API_BASE_URL}}/print-jobs/reservation-status', { API_BASE_URL: config.API_BASE_URL }),
          { params: { reservations: ids.join(',') } }
        );
        if (!cancelled && data && typeof data === 'object') setPrintStatusMap(data);
      } catch (e) {
        console.error('Failed to fetch print statuses', e);
      }
    };

    fetchPrintStatuses();
    const interval = setInterval(fetchPrintStatuses, 10000);
    return () => { cancelled = true; clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservations]);

  const tableRef = useRef(null);
  useCardifyTable(tableRef);

  // Treat "mobile" as phone-like (so tablets keep the same table layout in both orientations)
  const isMobileRaw = useIsMobile();
  const [viewport, setViewport] = useState(() => ({
    w: (typeof window !== "undefined" && window.innerWidth) ? window.innerWidth : 0,
    h: (typeof window !== "undefined" && window.innerHeight) ? window.innerHeight : 0,
  }));

  useEffect(() => {
    const onResize = () => {
      setViewport({
        w: window.innerWidth || 0,
        h: window.innerHeight || 0,
      });
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  const isMobile = useMemo(() => {
    const w = Number(viewport.w) || 0;
    const h = Number(viewport.h) || 0;

    // If we can't read viewport, fallback to the shared hook.
    if (!w || !h) return Boolean(isMobileRaw);

    const isPortrait = h >= w;

    // Phone portrait -> mobile/cards view.
    // Phone landscape -> use the tablet/desktop table view (as requested).
    if (isPortrait) return w <= 600;
    return false;
  }, [viewport.w, viewport.h, isMobileRaw]);

  const reservationsRef = useRef([]);
  useEffect(() => { reservationsRef.current = reservations; }, [reservations]);

  
  const { i18n: i18nextI18n } = useTranslation();
  const isGreek = (i18nextI18n.resolvedLanguage || i18nextI18n.language || "en")
    .split("-")[0] === "el";

  const renderStars = (rate) => {
    return '★'.repeat(rate) + '☆'.repeat(5 - rate);
  };

  const ResultsIcon = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ fill: "#0aa" }}>
      <path d="M4 19h16v2H4v-2zM6 10h3v7H6v-7zm5-4h3v11h-3V6zm5 7h3v4h-3v-4z"/>
    </svg>
  );

  useEffect(() => {
    axios
      .get(tr('{{API_BASE_URL}}/areas', { API_BASE_URL: config.API_BASE_URL }))
      .then(res => setAreasList(Array.isArray(res.data) ? res.data : []))
      .catch(() => setAreasList([]));
  }, []);

  useEffect(() => {
    axios
      .get(tr('{{API_BASE_URL}}/users/plan-permissions', {
        API_BASE_URL: config.API_BASE_URL
      }))
      .then((res) => {
        const { active, planId: rawPlanId } = res.data || {};
        if (!active || rawPlanId == null) return;
        setPlanId(Number(rawPlanId));
      })
      .catch((err) => {
        console.error("Error fetching plan info in ReservationsList:", err);
      });
  }, []);

  // Fetch company reservation duration (used for availability windows)
  useEffect(() => {
    let cancelled = false;
    axios
      .get(
        tr("{{API_BASE_URL}}/_companies/settings", { API_BASE_URL: config.API_BASE_URL }),
        { withCredentials: true, timeout: 15000 }
      )
      .then((res) => {
        if (cancelled) return;
        const v = Number(res?.data?.reservation_duration);
        setReservationDurationMinutes(Number.isFinite(v) && v > 0 ? v : 120);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch base tables once (needed for the read-only mini TableSelector snapshot)
  useEffect(() => {
    if (isMobile) return;
    let cancelled = false;

    axios
      .get(tr("{{API_BASE_URL}}/tables", { API_BASE_URL: config.API_BASE_URL }), {
        withCredentials: true,
        timeout: 15000,
      })
      .then((res) => {
        if (cancelled) return;
        setAllTables(Array.isArray(res?.data) ? res.data : []);
      })
      .catch((err) => {
        console.error("Error fetching tables for mini availability:", err);
        setAllTables([]);
      });

    return () => {
      cancelled = true;
    };
  }, [isMobile]);

  const miniDateKeys = useMemo(() => {
    if (isMobile) return [];
    const set = new Set();
    (filteredReservations || []).forEach((r) => {
      const d = parseToLocalDate(r?.arrival);
      if (!(d instanceof Date) || isNaN(d)) return;
      const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      set.add(k);
    });
    return Array.from(set);
  }, [filteredReservations, isMobile]);

  // Fetch mini graph data + table calendar per shown date group (desktop/tablet only)
  useEffect(() => {
    if (isMobile) return;
    if (!allTables) return;
    if (!miniDateKeys.length) return;

    let cancelled = false;

    const ensureSeatGraph = async (dateKey) => {
      if (miniSeatGraphByDate[dateKey]) return;
      try {
        const url = tr("{{API_BASE_URL}}/reservations/calendar/{{date}}", {
          API_BASE_URL: config.API_BASE_URL,
          date: dateKey,
        });
        const res = await axios.get(url, { withCredentials: true, timeout: 15000 });
        if (cancelled) return;

        const virtual = [
          {
            capacity: allTables.reduce((s, t) => s + (Number(t?.capacity) || 0), 0),
            reservations: Array.isArray(res?.data) ? res.data : [],
          },
        ];
        const graph = buildSeatGraphData(virtual, reservationDurationMinutes);
        setMiniSeatGraphByDate((prev) => ({ ...prev, [dateKey]: graph }));
      } catch (e) {
        // silently ignore (e.g., no perms)
      }
    };

    const ensureTableCalendar = async (dateKey) => {
      if (miniTableCalendarByDate[dateKey]) return;
      try {
        const url = tr("{{API_BASE_URL}}/tables/calendar/{{date}}", {
          API_BASE_URL: config.API_BASE_URL,
          date: dateKey,
        });
        const res = await axios.get(url, { withCredentials: true, timeout: 15000 });
        if (cancelled) return;

        const byTableId = {};
        (res?.data || []).forEach((t) => {
          if (!t || t.id == null) return;
          byTableId[Number(t.id)] = Array.isArray(t.reservations) ? t.reservations : [];
        });
        setMiniTableCalendarByDate((prev) => ({ ...prev, [dateKey]: { byTableId } }));
      } catch (e) {
        // silently ignore
      }
    };

    miniDateKeys.forEach((k) => {
      ensureSeatGraph(k);
      ensureTableCalendar(k);
    });

    return () => {
      cancelled = true;
    };
  }, [
    isMobile,
    allTables,
    miniDateKeys,
    miniSeatGraphByDate,
    miniTableCalendarByDate,
    reservationDurationMinutes,
  ]);

  const scheduleCloseMiniHover = useCallback(() => {
    if (miniHoverCloseTimerRef.current) clearTimeout(miniHoverCloseTimerRef.current);
    miniHoverCloseTimerRef.current = setTimeout(() => {
      setMiniHover(null);
    }, 160);
  }, []);

  const cancelCloseMiniHover = useCallback(() => {
    if (miniHoverCloseTimerRef.current) clearTimeout(miniHoverCloseTimerRef.current);
    miniHoverCloseTimerRef.current = null;
  }, []);

  const miniHoverTablesOverride = useMemo(() => {
    if (!miniHover || !allTables?.length) return null;

    const dateKey = miniHover.dateKey;
    const label = miniHover.label;

    const byTableId = miniTableCalendarByDate?.[dateKey]?.byTableId;
    if (!byTableId) return null;

    const dur = Number(reservationDurationMinutes) || 120;

    // hovered time in minutes
    const parts = String(label).split(":");
    const hh = Number(parts[0] || 0);
    const mm = Number(parts[1] || 0);
    const tMin = hh * 60 + mm;

    const lower = tMin - dur;
    const upper = tMin + dur;

    return allTables.map((t) => {
      const rs = byTableId[Number(t.id)] || [];
      let best = null;
      let bestMin = null;

      rs.forEach((r) => {
        if (!r) return;
        if (r.completed != null) return; // keep consistent with /tables/availability
        const aMin = parseTimeMinutes(r.arrival);
        if (aMin == null) return;

        if (aMin > lower && aMin < upper) {
          if (bestMin == null || aMin < bestMin) {
            bestMin = aMin;
            best = r;
          }
        }
      });

      if (best) {
        return {
          ...t,
          isAvailable: false,
          nextReservation: toHHMM(bestMin),
          reserved_id: best.id,
          reserved_people: best.people,
        };
      }

      return {
        ...t,
        isAvailable: true,
        nextReservation: "",
        reserved_id: null,
        reserved_people: null,
      };
    });
  }, [miniHover, allTables, miniTableCalendarByDate, reservationDurationMinutes]);

  const miniHoverHeaderNode = useMemo(() => {
    if (!miniHover) return null;

    const g = miniSeatGraphByDate?.[miniHover.dateKey];
    const dateEl = <span className="rlMiniAvailStrong">{miniHover.dateKey}</span>;
    const selTimeEl = <span className="rlMiniAvailStrong">{miniHover.label}</span>;

    if (!g?.slots?.length || !g.totalCapacity) {
      return (
        <>
          {dateEl} • {selTimeEl}
        </>
      );
    }

    const areaUsed = getAreaUsedPercent(g);
    const mx = getMaxInfo(g);

    const slot = g.slots.find((s) => s.label === miniHover.label);
    const selSeats = Number(slot?.value ?? miniHover.reservedSeats ?? 0);
    const selPct = pct(selSeats, g.totalCapacity, 0);

    return (
      <>
        {dateEl}: {areaUsed}% - {tr("Max")} @{mx.time}: {mx.value} ({mx.percent}%) - {tr("Selection")} @
        {selTimeEl}: {selSeats} ({selPct}%)
      </>
    );
  }, [miniHover, miniSeatGraphByDate]);

  useLayoutEffect(() => {
    if (!miniHover || !miniHoverTablesOverride) return;

    let raf = requestAnimationFrame(() => {
      try {
        const el = miniScaleRef.current;
        if (el) {
          // scrollWidth/scrollHeight are NOT affected by CSS transform (scale),
          // so they reflect the "base" unscaled TableSelector size.
          const w = Math.max(100, el.scrollWidth || el.offsetWidth || 0);
          const h = Math.max(100, el.scrollHeight || el.offsetHeight || 0);
          setMiniBaseSize((prev) => {
            const pw = Number(prev?.w) || 0;
            const ph = Number(prev?.h) || 0;
            return Math.abs(pw - w) > 2 || Math.abs(ph - h) > 2 ? { w, h } : prev;
          });
        }

        const head = miniHeadRef.current;
        if (head) {
          const hh = Math.max(36, Math.round(head.getBoundingClientRect().height || 0));
          setMiniHeadH((prev) => (Math.abs((Number(prev) || 0) - hh) > 1 ? hh : prev));
        }
      } catch {}
    });

    return () => cancelAnimationFrame(raf);
  }, [miniHover, miniHoverTablesOverride]);



  useEffect(() => {
    const fetchStates = async () => {
      try {
        const { data } = await axios.get(tr('{{API_BASE_URL}}/states', {
          API_BASE_URL: config.API_BASE_URL
        }));
        setStates(data);
      } catch (error) {
        console.error('Error fetching states:', error);
      }
    };

    fetchStates();
  }, []);

  useEffect(() => {
    const fetchReservations = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(tr('{{API_BASE_URL}}/reservations', {
          API_BASE_URL: config.API_BASE_URL
        }));
        const enrichedReservations = await Promise.all(
          data.map(async (reservation) => {
            try {
              const { data: tables } = await axios.get(
                tr('{{API_BASE_URL}}/assignedtables/reservation/{{id}}', {
                  API_BASE_URL: config.API_BASE_URL,
                  id: reservation.id,
                })
              );
        
               const nums = Array.isArray(tables)
                 ? tables
                     .map((t) => (t?.number == null ? "" : String(t.number).trim()))
                     .filter(Boolean)
                 : [];
          
               // Natural/numeric sort: "2" before "10", also works for "A2" < "A10"
               const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
               nums.sort(collator.compare);
          
               return { ...reservation, tables: nums.join(", ") };
            } catch (e) {
              // IMPORTANT: don't break the whole list refresh if ONE reservation has no assigned tables
              return { ...reservation, tables: '' };
            }
          })
        );
        
        setReservations(enrichedReservations);

      } catch (error) {
        console.error('Error fetching reservations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchReservations();
  }, [isModalOpen, isMeatsModalOpen, refresh]);

  // Refresh assigned states every minute (currently 10s)
  useEffect(() => {
    const interval = setInterval(() => {
      const refreshAssignedStates = async () => {
        try {
          const { data } = await axios.get(tr('{{API_BASE_URL}}/reservations', { API_BASE_URL: config.API_BASE_URL }));
          
          const prev = reservationsRef.current || [];
          const prevIds = new Set(prev.map(r => Number(r.id)));
          const dataIds = new Set((data || []).map(r => Number(r.id)));
          
          const hasNew = (data || []).some(r => !prevIds.has(Number(r.id)));
          const hasGone = prev.some(r => !dataIds.has(Number(r.id)));
          
          if (hasNew || hasGone) {
            setRefresh(r => !r); // triggers your existing full fetchReservations (with tables enrichment)
            return;
          }
          
          // otherwise keep your lightweight “state-only” update
          setReservations(prevReservations =>
            prevReservations.map(reservation => {
              const updatedReservation = data.find(res => res.id === reservation.id);
              return updatedReservation
                ? {
                    ...reservation,
                    state_id: updatedReservation.state_id,
                    feedback_id: updatedReservation.feedback_id,
                    feedback_org_service: updatedReservation.feedback_org_service,
                    feedback_quality_cost: updatedReservation.feedback_quality_cost,
                    feedback_environment_clean: updatedReservation.feedback_environment_clean,
                    feedback_comment: updatedReservation.feedback_comment,
                    feedback_created: updatedReservation.feedback_created
                  }
                : reservation;
            })
          );
        } catch (error) {
          console.error('Error refreshing assigned states:', error);
        }
      };

      refreshAssignedStates();
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let timer;
    const tick = async () => {
      try {
        const { data: cancelledIds } = await axios.get(
          tr('{{API_BASE_URL}}/reservations/cancelled/recent?minutes=5', {
            API_BASE_URL: config.API_BASE_URL
          })
        );
        if (Array.isArray(cancelledIds) && cancelledIds.length) {
          const idSet = new Set(cancelledIds.map(Number));
          setReservations(prev => prev.filter(r => !idSet.has(Number(r.id))));
        }
      } catch (e) {
        console.error('cancelled/recent poll failed', e);
      } finally {
        timer = setTimeout(tick, 2 * 60 * 1000);
      }
    };
    tick();
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    axios.get(tr('{{API_BASE_URL}}/meats', { API_BASE_URL: config.API_BASE_URL }))
      .then(res => setMeatsList(Array.isArray(res.data) ? res.data : []))
      .catch(() => setMeatsList([]));

    axios.get(tr('{{API_BASE_URL}}/cooktypes', { API_BASE_URL: config.API_BASE_URL }))
      .then(res => setCooktypesList(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCooktypesList([]));
  }, []);
  
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const meatNameById = useMemo(() => {
    const m = new Map();
    (meatsList || []).forEach(x => m.set(String(x.id), x.name));
    return m;
  }, [meatsList]);

  const cooktypeNameById = useMemo(() => {
    const m = new Map();
    (cooktypesList || []).forEach(x => m.set(String(x.id), x.name));
    return m;
  }, [cooktypesList]);

  const formatArrivalHHMM = useCallback((arrival) => {
    const d = parseToLocalDate(arrival);
    if (!(d instanceof Date) || isNaN(d)) return "";
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  }, []);
  
  const buildReservationStickerPayload = useCallback((reservation, assignedRows = []) => {
    const fullName = tr('{{lastname}} {{firstname}}', {
      lastname: reservation?.client_lastname || '',
      firstname: reservation?.client_firstname || ''
    }).trim();
  
    return {
      reservationId: reservation?.id ?? '',
      arrivalTime: formatArrivalHHMM(reservation?.arrival),
      tables: String(reservation?.tables || '').trim(),
      fullName,
      meats: (Array.isArray(assignedRows) ? assignedRows : []).map((am) => ({
        meatId: am?.meat ?? am?.id ?? '',
        quantity: am?.quantity ?? am?.qty ?? am?.grams ?? '',
        cookType:
          cooktypeNameById.get(String(am?.cooktype)) ||
          am?.cooktype_name ||
          String(am?.cooktype || ''),
        meatName:
          meatNameById.get(String(am?.meat)) ||
          am?.meat_name ||
          String(am?.meat || '')
      }))
    };
  }, [cooktypeNameById, formatArrivalHHMM, meatNameById]);

  const handlePrintReservationStickers = async (reservation) => {
    try {
      if (!reservation?.id) return;
      if (!Boolean(reservation.confirmed)) return;
  
      const { data } = await axios.get(
        tr('{{API_BASE_URL}}/assignedmeats/{{id}}', {
          API_BASE_URL: config.API_BASE_URL,
          id: reservation.id
        })
      );
  
      const assigned = Array.isArray(data) ? data : [];
      if (!assigned.length) {
        toast.info(tr('No assigned meats to print.'));
        return;
      }
  
      const payload = buildReservationStickerPayload(reservation, assigned);

      localStorage.setItem('labelPrinterHint', 'ZD220');
      localStorage.setItem('labelOrientation', 'N');

      await enqueuePrintJob({
        payload,
        meatIds: assigned.map((am) => am?.id).filter(Boolean),
        reservation: reservation.id,
      });

      setPrintStatusMap((prev) => ({ ...prev, [reservation.id]: 1 })); // queued -> yellow

      toast.success(
        tr('Stickers sent: {{n}}', { n: assigned.length }),
        { autoClose: 2500 }
      );
    } catch (e) {
      console.error('Print reservation stickers failed', e);
      toast.error(tr('Failed to queue the sticker for printing.'));
    }
  };

  const isArrivalWithinNextHour = (arrival) => {
    const d = parseToLocalDate(arrival);
    if (!(d instanceof Date) || isNaN(d)) return false;
    const diffMs = d.getTime() - Date.now();
    return diffMs > 0 && diffMs <= 60 * 60 * 1000;
  };

  // today in local Y-M-D
  const todayISO = useMemo(() => {
    const d = startOfToday();
    return tr('{{var1}}-{{var2}}-{{var3}}', {
      var1: d.getFullYear(),
      var2: String(d.getMonth()+1).padStart(2,'0'),
      var3: String(d.getDate()).padStart(2,'0')
    });
  }, []);

  const tomorrowISO = useMemo(() => {
    const d = startOfToday();
    d.setDate(d.getDate() + 1);
    return tr('{{y}}-{{m}}-{{dd}}', {
      y: d.getFullYear(),
      m: String(d.getMonth() + 1).padStart(2, '0'),
      dd: String(d.getDate()).padStart(2, '0')
    });
  }, []);

  // List of available dates (>= today) that have reservations
  const enabledDates = useMemo(() => {
    if (!Array.isArray(reservations) || !reservations.length) return [];
    const set = new Set();
    reservations.forEach(r => {
      const d = parseToLocalDate(r.arrival);
      if (isNaN(d)) return;
      const ymd = tr('{{var1}}-{{var2}}-{{var3}}', {
        var1: d.getFullYear(),
        var2: String(d.getMonth()+1).padStart(2,'0'),
        var3: String(d.getDate()).padStart(2,'0')
      });
      if (ymd >= todayISO) set.add(ymd);
    });
    return Array.from(set).sort();
  }, [reservations, todayISO]);

  const enabledDateObjs = useMemo(() => {
    return enabledDates.map(s => {
      const [y,m,d] = s.split('-').map(Number);
      return new Date(y, (m||1)-1, d||1);
    });
  }, [enabledDates]);

  const miniLayout = useMemo(() => {
    if (!miniHover?.anchorRect) return null;
    const baseW = miniBaseSize?.w || 1050;
    const baseH = miniBaseSize?.h || 720;
    const headH = miniHeadH || 48;
    return computeMiniAvailTooltipLayout({
      graphRect: miniHover.anchorRect,
      baseW,
      baseH,
      headH,
    });
  }, [miniHover, miniBaseSize, miniHeadH]);

  const getDateKey = (d) => (
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  );

  const formatGroupTitle = (dateObj) => {
    const locale = isGreek ? "el-GR" : "en-GB";
    const weekday = new Intl.DateTimeFormat(locale, { weekday: "long" }).format(dateObj);
    const dayMonth = new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit" }).format(dateObj);
    return `${weekday} ${dayMonth}`;
  };

  const formatGroupWeekdayOnly = (dateObj) => {
    const locale = isGreek ? "el-GR" : "en-GB";
    return new Intl.DateTimeFormat(locale, { weekday: "long" }).format(dateObj);
  };


  const formatGroupDateOnly = (dateObj) => {
    const locale = isGreek ? "el-GR" : "en-GB";
    return new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit" }).format(dateObj);
  };

  const formatArrivalCell = (arrival) => {
    const d = parseToLocalDate(arrival);
    if (!(d instanceof Date) || isNaN(d)) return "";
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  };

  const dayStats = useMemo(() => {
    const stats = new Map();
    (filteredReservations || []).forEach((r) => {
      const d = parseToLocalDate(r.arrival);
      if (!(d instanceof Date) || isNaN(d)) return;
      const key = getDateKey(d);

      if (!stats.has(key)) {
        stats.set(key, {
          dateObj: new Date(d.getFullYear(), d.getMonth(), d.getDate()),
          reservations: 0,
          people: 0,
          tables: 0, // count table assignments (not unique tables)
        });
      }
      const s = stats.get(key);
      s.reservations += 1;
      s.people += Number(r.people || 0);

      // IMPORTANT:
      // We want "Tables" totals to count assignments, even if the same table is used again later in the day.
      // So we sum the tables-per-reservation (deduped per reservation row just in case).
      const parts = String(r.tables || "")
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);

      const uniqueParts = Array.from(new Set(parts));
      s.tables += uniqueParts.length;
    });

    const obj = {};
    stats.forEach((v, k) => {
      obj[k] = v;
    });
    return obj;
  }, [filteredReservations, isGreek]);


  const handleFilterModeChange = (e) => {
    setFilterMode(e.target.value);
    setSelectedDate(null);
    try {
      suppressFpChangeRef.current = true;
      fpInstanceRef.current?.clear?.();
      fpInstanceRef.current?.close?.();
      suppressFpChangeRef.current = false;
    } catch {}
  };

  const toggleConfirmed = async (reservation) => {
    try {
      const next = !Boolean(reservation.confirmed);
      const { data } = await axios.post(
        tr('{{API_BASE_URL}}/reservations/{{id}}/confirm', {
          API_BASE_URL: config.API_BASE_URL,
          id: reservation.id
        }),
        { confirm: next }
      );
      setReservations(prev =>
        prev.map(r => r.id === reservation.id ? { ...r, confirmed: data.confirmed } : r)
      );
    } catch (e) {
      console.error('failed to toggle confirmed', e);
      alert(tr('Failed to update confirmation.'));
    }
  };

  const handleStateChange = async (reservationId, newState) => {
    try {
      await axios.post(tr('{{API_BASE_URL}}/assignedstates/{{reservationId}}', {
        API_BASE_URL: config.API_BASE_URL,
        reservationId
      }), {
        reservation: reservationId,
        state: newState,
      });

      setReservations((prevReservations) =>
        prevReservations.map((res) =>
          res.id === reservationId ? { ...res, state_id: parseInt(newState, 10) } : res
        )
      );
    } catch (error) {
      console.error('Error updating reservation state:', error);
      alert(tr('Failed to update reservation state.'));
    }
  };

  const handleEdit = async (reservationId) => {
    const reservation = reservations.find((res) => res.id === reservationId);
    const localDate = parseToLocalDate(reservation.arrival);
    const dateIso = tr('{{var1}}-{{var2}}-{{var3}}', {
      var1: localDate.getFullYear(),
      var2: String(localDate.getMonth() + 1).padStart(2, "0"),
      var3: String(localDate.getDate()).padStart(2, "0")
    });
    const hhmm = tr('{{var1}}:{{var2}}', {
      var1: String(localDate.getHours()).padStart(2, "0"),
      var2: String(localDate.getMinutes()).padStart(2, "0")
    });

    setSelectedReservation({
      ...reservation,
      date: dateIso,
      reservationTime: hhmm,
    });

    try {
      const { data } = await axios.get(
        tr('{{API_BASE_URL}}/assignedtables/reservation/{{reservationId}}', {
          API_BASE_URL: config.API_BASE_URL,
          reservationId
        })
      );
      setAssignedTables(data.map((table) => table.table));
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error fetching assigned tables:', error);
    }
  };

  const handleAssignMeats = (reservationId) => {
    const reservation = reservations.find((res) => res.id === reservationId);
    setSelectedReservation(reservation);
    setIsMeatsModalOpen(true);
  };

  const handleCancel = async (reservationId) => {
    if (window.confirm(tr('Are you sure you want to cancel reservation ')+reservationId+tr(' ?'))) {
      try {
        await axios.delete(tr('{{API_BASE_URL}}/reservations/{{reservationId}}', {
          API_BASE_URL: config.API_BASE_URL,
          reservationId
        }));
        setReservations((prevReservations) =>
          prevReservations.filter((res) => res.id !== reservationId)
        );
      } catch (error) {
        console.error('Error canceling reservation:', error);
        alert(tr('Failed to cancel the reservation.'));
      }
    }
  };

  useEffect(() => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);

    const filtered = reservations.filter((reservation) => {
      const arrival = parseToLocalDate(reservation.arrival);
      if (filterMode === 'today') {
        return arrival >= todayStart && arrival < todayEnd;
      } else if (filterMode === 'future') {
        return arrival >= todayEnd;
      } else if (filterMode === 'walkedin') {
        return !reservation.client && arrival >= todayStart && arrival < todayEnd;
      } else if (filterMode === 'reserved') {
        return reservation.client && arrival >= todayStart && arrival < todayEnd;
      }
      return true;
    });

    const searchLower = searchQuery.toLowerCase();
    const searchFiltered = filtered.filter((reservation) =>
      !reservation.client ||
      reservation.client_firstname?.toLowerCase().includes(searchLower) ||
      reservation.client_lastname?.toLowerCase().includes(searchLower) ||
      reservation.client_phone?.toLowerCase().includes(searchLower) ||
      reservation.comment?.toLowerCase().includes(searchLower)
    );

    const byDate = !selectedDate ? searchFiltered : searchFiltered.filter((r) => {
      const d = parseToLocalDate(r.arrival);
      if (isNaN(d)) return false;
      const ymd = tr('{{var1}}-{{var2}}-{{var3}}', {
        var1: d.getFullYear(),
        var2: String(d.getMonth()+1).padStart(2,'0'),
        var3: String(d.getDate()).padStart(2,'0')
      });
      return ymd === selectedDate;
    });

    setFilteredReservations(byDate);
  }, [filterMode, reservations, searchQuery, selectedDate]);

  const fpOptions = useMemo(() => {
    const base = {
      dateFormat: "d/m/y",
      minDate: startOfToday(),
      allowInput: true,
    };
    if (isGreek && GreekLocale) base.locale = GreekLocale;
    if (enabledDateObjs.length) base.enable = enabledDateObjs;
    return base;
  }, [enabledDateObjs, isGreek]);

  const isBuzzerEligible = (stateId) => {
    const n = Number(stateId || 0);
    return n >= 5 && n <= 12;
  };

  const openQrForReservation = async (reservation) => {
    try {
      if (!reservation?.id) return;
      const { data } = await axios.get(
        tr("{{API_BASE_URL}}/reservations/{{id}}/buzzer-link", {
          API_BASE_URL: config.API_BASE_URL,
          id: reservation.id
        })
      );
      const link = data?.publicUrl || "";
      if (!link) throw new Error("No link");

      setQrForReservation(reservation);
      setQrUrl(link);
      const currentLang = (i18nextI18n.resolvedLanguage || i18nextI18n.language || "en").split("-")[0];
      setQrLang(currentLang === "el" ? "el" : "en");
      setIsQrModalOpen(true);
    } catch (e) {
      console.error("openQrForReservation failed", e);
      toast.error(tr("Failed to generate QR link"));
    }
  };

  const qrFinalUrl = useMemo(() => {
    if (!qrUrl) return "";
    const sep = qrUrl.includes("?") ? "&" : "?";
    return `${qrUrl}${sep}lang=${encodeURIComponent(qrLang || "en")}`;
  }, [qrUrl, qrLang]);

  const QrIcon = ({ size = 22 }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      style={{ fill: "#444" }}
    >
      <path d="M3 3h8v8H3V3zm2 2v4h4V5H5z" />
      <path d="M13 3h8v8h-8V3zm2 2v4h4V5h-4z" />
      <path d="M3 13h8v8H3v-8zm2 2v4h4v-4H5z" />
      <path d="M13 13h2v2h-2v-2zm3 0h2v3h-2v-3zm3 0h2v2h-2v-2z" />
      <path d="M13 16h3v2h-3v-2zm4 1h2v2h-2v-2z" />
      <path d="M19 16h2v5h-5v-2h3v-3z" />
      <path d="M13 19h2v2h-2v-2z" />
    </svg>
  );

  const AutoIcon = ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ fill: "currentColor" }}>
      <path d="M12 2l1.4 5.2L18 9l-4.6 1.8L12 16l-1.4-5.2L6 9l4.6-1.8L12 2zm7 8l.9 3.2L23 14l-3.1 1.2L19 18l-.9-2.8L15 14l3.1-.8L19 10zM4 12l1 3.6L8.5 17 5 18.3 4 22l-1-3.7L0 17l3-1.4L4 12z"/>
    </svg>
  );

  const ListIcon = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ fill: "currentColor" }}>
      <path d="M4 6h2v2H4V6zm4 0h14v2H8V6zM4 11h2v2H4v-2zm4 0h14v2H8v-2zM4 16h2v2H4v-2zm4 0h14v2H8v-2z"/>
    </svg>
  );

  const TableIcon = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ fill: "currentColor" }}>
      <path d="M4 10h16V7H4v3zm0 2v5h2v-3h12v3h2v-5H4z"/>
    </svg>
  );

  const PeopleIcon = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ fill: "currentColor" }}>
      <path d="M16 11c1.7 0 3-1.3 3-3s-1.3-3-3-3-3 1.3-3 3 1.3 3 3 3zm-8 0c1.7 0 3-1.3 3-3S9.7 5 8 5 5 6.3 5 8s1.3 3 3 3zm0 2c-2.3 0-6 1.2-6 3.5V20h12v-3.5C14 14.2 10.3 13 8 13zm8 0c-.3 0-.6 0-.9.1 1.2.9 1.9 2 1.9 3.4V20h6v-3.5c0-2.3-3.7-3.5-7-3.5z"/>
    </svg>
  );

  return (
    <div className="reservationsPage">

      {/* Sticky toolbar (search/filters/buttons stay visible) */}
      <div className="reservationsToolbar">
        <div style={{width: '95%', margin: 'auto'}}>
          <h2 style={{display: 'flex', alignItems: 'center', gap: '10px', whiteSpace: 'nowrap', marginBottom: isMobile?'10px':'25px'}}>
            <span style={{marginRight: isMobile?'5px':'15px'}}>
              {tr('Reservations')}{' '}{isMobile?'':(tr('List')+(filteredReservations.length ? ': '+filteredReservations.length: ''))}
            </span>

            {!isMobile && (
              <>
                <input
                  type="text"
                  placeholder={tr('Search reservations by name, phone, or comment')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ marginRight: '15px', padding: '10px', width: '100%' }}
                />
              </>
            )}

            <Flatpickr
              options={fpOptions}
              placeholder={tr('Calendar')}
              onReady={(_dates, _str, inst) => { fpInstanceRef.current = inst; }}
              onOpen={(_dates, _str, inst) => {
                try { if (inst?.selectedDates?.length) inst.clear(); } catch {}
                setSelectedDate(null);
                setFilterMode('all');
              }}
              onChange={(dates) => {
                if (suppressFpChangeRef.current) { return; }
                if (!Array.isArray(dates) || dates.length === 0) { setSelectedDate(null); return; }
                const dt = dates[0];
                if (!(dt instanceof Date) || isNaN(dt)) { setSelectedDate(null); return; }
                const y = dt.getFullYear();
                const m = String(dt.getMonth()+1).padStart(2,'0');
                const d = String(dt.getDate()).padStart(2,'0');
                setSelectedDate(tr('{{y}}-{{m}}-{{d}}', { y, m, d }));
                setFilterMode('all');
              }}
              style={{
                padding: '9px 9px',
                border: '1px solid gray',
                borderRadius: 6,
                width: 55
              }}
            />

            <select
              value={filterMode}
              onClick={() => {
                setSelectedDate(null);
                try {
                  suppressFpChangeRef.current = true;
                  fpInstanceRef.current?.clear?.();
                  fpInstanceRef.current?.close?.();
                  suppressFpChangeRef.current = false;
                } catch {}
              }}
              onChange={handleFilterModeChange}
              style={{ padding: '5px', borderRadius: '5px', width: isMobile?'98%':'170px', height: '35px'}}
            >
              <option value="all">{tr('All Reservations')}</option>
              <option value="today">{tr('Today\'s All')}</option>
              <option value="reserved">{tr('Today\'s Reservations')}</option>
              <option value="walkedin">{tr('Today\'s WalkedIn')}</option>
              <option value="future">{tr('Future Reservations')}</option>
            </select>

            {!isMobile && (
              <>

                <WalkedInSitting areasList={areasList} onRefresh={onRefreshStable}/>
                <button
                  className="rlToolbarBtn"
                  onClick={() => setHistoryOpen(true)}
                  title={tr('History')}
                >
                  {"\u{1F4DC}"} {tr('History')}
                </button>
                {isAdmin && (
                  <button
                    className="rlToolbarBtn"
                    onClick={() => setAutoAssignOpen(true)}
                    title={tr('Smart table assignment')}
                  >
                    {"✨"} {tr('Auto assign')}
                  </button>
                )}
              </>
            )}
          </h2>

          {isMobile && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                marginBottom: 6,
              }}
            >
              {filteredReservations.length > 0 && (
                <i style={{ whiteSpace: 'nowrap', flex: '0 0 auto', opacity: 0.75, fontSize: 12 }}>
                  {filteredReservations.length} {tr('results')}
                </i>
              )}

              <input
                type="text"
                placeholder={tr('Search reservations by name, phone, or comment')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  padding: '8px',
                  flex: '1 1 auto',
                  minWidth: 0,
                }}
              />

              <WalkedInSitting
                areasList={areasList}
                onRefresh={onRefreshStable}
                toolbarVariant="icons"
              />

              <button
                className="rlToolbarBtn"
                onClick={() => setHistoryOpen(true)}
                title={tr('History')}
                style={{ flex: '0 0 auto' }}
              >
                {"\u{1F4DC}"}
              </button>

              {isAdmin && (
                <button
                  className="rlToolbarBtn"
                  onClick={() => setAutoAssignOpen(true)}
                  title={tr('Smart table assignment')}
                  style={{ flex: '0 0 auto' }}
                >
                  {"✨"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Scrollable list area */}
      <div className="reservationsScrollArea">
        {/* Mobile should use full width (with small side padding) without horizontal scrolling. */}
        <div style={{ width: isMobile ? '100%' : '95%', margin: 'auto', padding: isMobile ? '0 6px' : 0 }}>

          <table ref={tableRef} className="cardify" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr>
                <th>{tr('ID')}</th>
                <th style={{ color: isMobile ? 'black' : 'darkOrange' }}>
                  {isMobile ? tr('Rate') : '★'}
                </th>
                <th>{tr('Client')}</th>
                <th>{tr('Phone')}</th>
                <th>{tr('People')}</th>
                <th data-col="arrival">{tr('Arrival')}</th>
                <th>{tr('Tables')}</th>
                <th>{tr('Comment')}</th>
                <th style={{textAlign: 'center'}}>{tr('View or Edit')}</th>
                <th>{tr('Status')}</th>
                {!isStarterPlan && (<th>{tr('Confirmed')}</th>)}
                <th>{tr('Cancel')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={isStarterPlan ? 11 : 12} style={{ textAlign: "center" }}>
                    <div className="loader"></div>
                    <h3>{tr('Loading reservations...')}</h3>
                  </td>
                </tr>
              ) : !filteredReservations.length ? (
                <tr>
                  <td colSpan={isStarterPlan ? 11 : 12} style={{ textAlign: "center" }}>
                    <h3>{tr('No reservations found yet.')}</h3>
                  </td>
                </tr>
              ) : (
                filteredReservations.map((reservation, idx) => {
                  const arrivalDate = parseToLocalDate(reservation.arrival);
                  if (!(arrivalDate instanceof Date) || isNaN(arrivalDate)) return null;

                  const dateKey = `${arrivalDate.getFullYear()}-${String(arrivalDate.getMonth() + 1).padStart(2, '0')}-${String(arrivalDate.getDate()).padStart(2, '0')}`;

                  const prevArrivalDate = idx > 0 ? parseToLocalDate(filteredReservations[idx - 1].arrival) : null;
                  const prevDateKey =
                    prevArrivalDate instanceof Date && !isNaN(prevArrivalDate)
                      ? `${prevArrivalDate.getFullYear()}-${String(prevArrivalDate.getMonth() + 1).padStart(2, '0')}-${String(prevArrivalDate.getDate()).padStart(2, '0')}`
                      : null;

                  const isNewDateGroup = idx === 0 || (prevDateKey && dateKey !== prevDateKey);
                  const ds = dayStats[dateKey];

                  const isToday = dateKey === todayISO;
                  const isTomorrow = dateKey === tomorrowISO;
                  const toneClass = isToday ? "dgToneToday" : isTomorrow ? "dgToneTomorrow" : "dgToneOther";

                  const arrivalPlus30minutes = new Date(arrivalDate.getTime() + 30 * 60 * 1000);
                  const isExpired = arrivalPlus30minutes < new Date() && (reservation.state_id ?? 0) < 5;

                  const isCollapsed = Boolean(collapsedDays[dateKey]);
                  const isAnimating = Boolean(dayAnimating[dateKey]);
                                    const isExpanding = Boolean(dayExpanding[dateKey]);
const shouldHideRows = isCollapsed && !isAnimating;

                  const groupLabel =
                    isToday || isTomorrow
                      ? (isToday ? tr("Today") : tr("Tomorrow"))
                      : (ds?.dateObj ? formatGroupWeekdayOnly(ds.dateObj) : dateKey);

                  const groupDateText = ds?.dateObj ? formatGroupDateOnly(ds.dateObj) : dateKey;

                  return (
                    <React.Fragment key={reservation.id}>
                      {isNewDateGroup && (
                        isMobile ? (
                          <tr
                            className={`date-group-row ${toneClass} ${isCollapsed ? "is-collapsed" : ""} ${isAnimating ? "is-animating" : ""}`}
                            onClick={() => toggleDayGroup(dateKey)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                toggleDayGroup(dateKey);
                              }
                            }}
                          >
                            <td colSpan={isStarterPlan ? 11 : 12} className="dateGroupCell">
                              <div className="dateStripBar dateStripBar--mobile">
                                <div className="dgBadge">
                                  <span className="dgLabel">{groupLabel}</span>
                                </div>

                                <div className="dateStripDate" title={dateKey}>
                                  <span className="dateGroupDateText">{groupDateText}</span>
                                </div>

                                <div className="dateStripTotals">
                                  <span className="dateMetric" title={tr("Reservations")}>
                                    <ListIcon /> {ds?.reservations ?? 0}
                                  </span>
                                  <span className="dateMetric" title={tr("Tables")}>
                                    <TableIcon /> {ds?.tables ?? 0}
                                  </span>
                                  <span className="dateMetric" title={tr("People")}>
                                    <PeopleIcon /> {ds?.people ?? 0}
                                  </span>
                                </div>

                                <span className="dateStripChevron" aria-hidden="true">
                                  {isCollapsed ? "▸" : "▾"}
                                </span>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          <tr
                            className={`date-group-row ${toneClass} ${isCollapsed ? "is-collapsed" : ""} ${isAnimating ? "is-animating" : ""}`}
                            onClick={() => toggleDayGroup(dateKey)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                toggleDayGroup(dateKey);
                              }
                            }}
                          >
                            {/* 1) ID (day badge) */}
                            <td className="dgCell dgCell--id">
                              <span className="dgBadge">
                                <span className="dgLabel">{groupLabel}</span>
                              </span>
                            </td>
                          
                            {/* 2) Rate */}
                            <td className="dgCell" />
                          
                            {/* 3) Client -> Reservations total */}
                            <td className="dgCell dgCell--metric" title={tr("Reservations")}>
                              <span className="dateMetric">
                                <ListIcon /> {ds?.reservations ?? 0}
                              </span>
                            </td>
                          
                            {/* 4) Phone */}
                            <td className="dgCell" />
                          
                            {/* 5) People -> People total */}
                            <td className="dgCell dgCell--metric" title={tr("People")}>
                              <span className="dateMetric">
                                <PeopleIcon /> {ds?.people ?? 0}
                              </span>
                            </td>
                          
                            {/* 6) Arrival (date only) */}
                            <td className="dgCell dgCell--arrival" title={dateKey}>
                              {groupDateText}
                            </td>
                          
                            {/* 7) Tables -> Tables total */}
                            <td className="dgCell dgCell--metric" title={tr("Tables")}>
                              <span className="dateMetric">
                                <TableIcon /> {ds?.tables ?? 0}
                              </span>
                            </td>
                          
                            {/* 8..(Comment / View / Status / Confirmed?) */}
                            <td className="dgCell dgCell--right" colSpan={isStarterPlan ? 3 : 4}>
                              <div
                                className="dgMiniGraphWrap"
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                onPointerDown={(e) => e.stopPropagation()}
                              >
                                <MiniAvailabilityGraph
                                  dateKey={dateKey}
                                  graph={miniSeatGraphByDate[dateKey]}
                                  activeLabel={miniHover?.dateKey === dateKey ? miniHover.label : null}
                                  onHover={(payload) => {
                                    cancelCloseMiniHover();
                                    setMiniHover((prev) => {
                                      if (!payload) return prev;
                                      if (prev?.dateKey === payload.dateKey && prev?.label === payload.label) return prev;
                                      return payload;
                                    });
                                  }}
                                  onLeave={scheduleCloseMiniHover}
                                />
                              </div>
                            </td>

                            {/* 9) Cancel column (keep chevron at the far right) */}
                            <td className="dgCell dgCell--chev">
                              <span className="dateStripChevron" aria-hidden="true">
                                {isCollapsed ? "▸" : "▾"}
                              </span>
                            </td>
                          </tr>
                        )
                      )}

                      {shouldHideRows ? null : (
                        <tr
                          className={`reservation-row ${isAnimating ? "is-collapsing" : ""} ${isExpanding ? "is-expanding" : ""}`}
                          style={{
                            borderBottom: "1px solid #ccc",
                            color: isExpired ? "darkRed" : "black",
                            transition: "transform 0.2s ease, font-weight 0.2s ease",
                            cursor: "pointer",
                          }}
                        >
                          <td style={{ textDecoration: isExpired ? "line-through" : "none" }}>{reservation.id}</td>

                          <td style={{ color: "darkorange" }}>
                            {(() => {
                              const rate = reservation.client_rate ?? 3;

                              if (isMobile) {
                                return (
                                  <span
                                    title={tr("Rating: {{rate}}", { rate })}
                                    style={{ fontSize: 8 + 2 * rate, gap: 15, opacity: rate === 3 ? "0.4" : "1" }}
                                  >
                                    {renderStars(rate)}
                                  </span>
                                );
                              }

                              const fontSize = 12 + rate * 2;
                              const fontWeight = 300 + rate * 100;

                              return (
                                <span
                                  title={tr("Rating: {{rate}}/5", { rate })}
                                  style={{ fontSize, fontWeight, opacity: rate === 3 ? "0.4" : "1" }}
                                >
                                  {rate}
                                </span>
                              );
                            })()}
                          </td>

                          <td style={{fontWeight: '600'}}>{reservation.client_lastname} {reservation.client_firstname}</td>
                          <td><a href={`tel:${reservation.client_phone}`}>{reservation.client_phone}</a></td>
                          <td style={{ textAlign: isMobile ? "left" : "center" }}>{reservation.people}</td>
                          <td style={{fontWeight: '600'}}>{formatArrivalCell(reservation.arrival)}</td>
                          <td>{reservation.tables}</td>
                          <td>{reservation.comment}</td>

                          <td style={{ textAlign: isMobile ? "left" : "center" }}>
                            <div style={{ display: "inline-flex", alignItems: "center", gap: 3, whiteSpace: "nowrap" }}>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleEdit(reservation.id); }}
                                title={tr("Reservation's info")}
                                style={{
                                  border: "none",
                                  background: "none",
                                  cursor: "pointer",
                                  padding: 0,
                                  width: 32,
                                  height: 32,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "larger"
                                }}
                              >
                                &#127915;
                              </button>

                              {!isStarterPlan && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleAssignMeats(reservation.id); }}
                                  title={tr("Assigned Meats")}
                                  style={{
                                    border: "none",
                                    background: "none",
                                    cursor: "pointer",
                                    padding: 0,
                                    width: 32,
                                    height: 32,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "larger"
                                  }}
                                >
                                  &#127830;
                                </button>
                              )}

                              {reservation.feedback_id ? (
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setFbReservation(reservation);
                                    setIsFbModalOpen(true);
                                  }}
                                  title={tr("Feedback")}
                                  style={{ border: "none", background: "none", cursor: "pointer", padding: 0, display: "inline-flex", alignItems: "center" }}
                                >
                                  <ResultsIcon size={20} />
                                </button>
                              ) : (
                                isBuzzerEligible(reservation.state_id) && (
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      openQrForReservation(reservation);
                                    }}
                                    title={tr("Buzzer QR")}
                                    style={{ border: "none", background: "none", cursor: "pointer", padding: 0, display: "inline-flex", alignItems: "center" }}
                                  >
                                    <QrIcon size={20} />
                                  </button>
                                )
                              )}
                            </div>
                          </td>

                          <td>
                            <select
                              className="statusSelect"
                              value={reservation.state_id || ""}
                              onChange={(e) => handleStateChange(reservation.id, e.target.value)}
                              style={{
                                border: "2px solid " + (states.find((state) => state.id === reservation.state_id)?.color || "gray"),
                              }}
                            >
                              {states.map((state) => (
                                <option key={state.id} value={state.id}>{tr(state.name)}</option>
                              ))}
                            </select>
                          </td>

                          {!isStarterPlan && (
                            <td>
                              {Number(reservation.meats_count) > 0 && (
                                <>
                                  <i>{reservation.meats_count}</i>
                                  {!isTodayLocal(reservation.arrival) ? " #" : (() => {
                                    const locked = Number(reservation.state_id) > 4;
                                    return (
                                      <>
                                        {":"}
                                        <input
                                          type="checkbox"
                                          checked={Boolean(reservation.confirmed)}
                                          disabled={locked}
                                          onChange={() => { if (!locked) toggleConfirmed(reservation); }}
                                          title={locked ? "Disabled after sitting" : "Assigned meats confirmed"}
                                          style={{ height: 17 }}
                                        />
                                        {Boolean(reservation.confirmed) && (() => {
                                          const printStatus = Number(printStatusMap[reservation.id] ?? 0);
                                          const toneClass =
                                            printStatus === 2 ? "print-stickers-green"
                                            : printStatus === 1 ? "print-stickers-yellow"
                                            : printStatus === 3 ? "print-stickers-orange"
                                            : "print-stickers-red";
                                          const className = "print-stickers-btn " + toneClass;

                                          return (
                                            <button
                                              className={className}
                                              title={
                                                printStatus === 2
                                                  ? tr("Stickers printed")
                                                  : printStatus === 1
                                                    ? tr("Sticker queued for printing")
                                                    : printStatus === 3
                                                      ? tr("Sticker printing failed")
                                                      : tr("Print stickers")
                                              }
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handlePrintReservationStickers(reservation);
                                              }}
                                            >
                                              {"\u{1F5A8}\u{FE0F}"}
                                            </button>
                                          );
                                        })()}
                                      </>
                                    );
                                  })()}
                                </>
                              )}
                            </td>
                          )}

                          <td>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleCancel(reservation.id); }}
                              style={{ border: "none", background: "white", marginLeft: "10px", cursor: "pointer" }}
                            >
                              <b style={{ color: isExpired ? "red" : "darkRed", textShadow: "2px 1px 2px gray" }}>{tr("⛌")}</b>
                            </button>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
              
              {!loading && filteredReservations.length > 0 && (
                <tr className="endOfListRow">
                  <td colSpan={isStarterPlan ? 11 : 12}>
                    <div className="endOfListSpacer">
                      <span>{tr("End of list")}</span>
                    </div>
                  </td>
                </tr>
              )}
              
            </tbody>
          </table>
        </div>
      </div>

      {/* Smart auto-assignment modal (admins only) */}
      {isAdmin && (
        <AutoAssignModal
          isOpen={autoAssignOpen}
          onClose={() => setAutoAssignOpen(false)}
          onApplied={() => setRefresh((r) => !r)}
          isMobile={isMobile}
        />
      )}

      {/* History modal: past reservations + cancellations/no-shows by date */}
      <Modal
        isOpen={historyOpen}
        onRequestClose={() => setHistoryOpen(false)}
        style={{
          overlay: { backgroundColor: 'rgba(0,0,0,0.35)', zIndex: 1000 },
          content: isMobile ? {
            inset: '0px', margin: 'auto', padding: '10px', border: '0',
            borderRadius: 10, height: '85vh', maxWidth: '94vw', maxHeight: '85vh', overflow: 'auto'
          } : {
            width: '860px', margin: 'auto', overflowX: 'hidden', borderRadius: 10, height: '82vh'
          }
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>{"\u{1F4DC}"} {tr('Reservations history')}</h3>
          <input
            type="date"
            value={historyDate}
            onChange={(e) => e.target.value && setHistoryDate(e.target.value)}
            style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid gray' }}
          />
          {historyLoading && <span style={{ opacity: 0.6 }}>{tr('Loading...')}</span>}
          <button
            onClick={() => setHistoryOpen(false)}
            style={{ marginLeft: 'auto', padding: '6px 12px', borderRadius: 6, border: '1px solid gray', background: 'white', cursor: 'pointer' }}
          >
            {tr('Close')}
          </button>
        </div>

        <h4 style={{ margin: '10px 0 6px' }}>
          {tr('Reservations')} ({historyData.reservations.length})
        </h4>
        {historyData.reservations.length === 0 && !historyLoading && (
          <p style={{ opacity: 0.6 }}>{tr('No reservations for this date.')}</p>
        )}
        {historyData.reservations.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid #ccc' }}>
                  <th style={{ padding: '4px 6px' }}>#</th>
                  <th style={{ padding: '4px 6px' }}>{tr('Arrival')}</th>
                  <th style={{ padding: '4px 6px' }}>{tr('Client')}</th>
                  <th style={{ padding: '4px 6px' }}>{tr('People')}</th>
                  <th style={{ padding: '4px 6px' }}>{tr('Tables')}</th>
                  <th style={{ padding: '4px 6px' }}>{tr('Meats')}</th>
                  <th style={{ padding: '4px 6px' }}>{tr('State')}</th>
                </tr>
              </thead>
              <tbody>
                {historyData.reservations.map((r) => (
                  <tr key={`hres-${r.id}`} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '4px 6px' }}>{r.id}</td>
                    <td style={{ padding: '4px 6px' }}>{formatDateTimeGR(r.arrival)}</td>
                    <td style={{ padding: '4px 6px' }}>{`${r.client_lastname || ''} ${r.client_firstname || ''}`.trim() || '—'}</td>
                    <td style={{ padding: '4px 6px' }}>{r.people ?? ''}</td>
                    <td style={{ padding: '4px 6px' }}>{r.tables || '—'}</td>
                    <td style={{ padding: '4px 6px' }}>{r.meats_count || 0}</td>
                    <td style={{ padding: '4px 6px' }}>
                      <span style={{ color: r.state_color || 'inherit', fontWeight: 600 }}>
                        {r.state_name || (r.completed ? tr('Completed') : tr('Reserved'))}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <h4 style={{ margin: '18px 0 6px' }}>
          {tr('Cancellations & no-shows')} ({historyData.cancellations.length})
        </h4>
        {historyData.cancellations.length === 0 && !historyLoading && (
          <p style={{ opacity: 0.6 }}>{tr('No cancellations for this date.')}</p>
        )}
        {historyData.cancellations.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid #ccc' }}>
                  <th style={{ padding: '4px 6px' }}>#</th>
                  <th style={{ padding: '4px 6px' }}>{tr('Arrival')}</th>
                  <th style={{ padding: '4px 6px' }}>{tr('Client')}</th>
                  <th style={{ padding: '4px 6px' }}>{tr('People')}</th>
                  <th style={{ padding: '4px 6px' }}>{tr('Type')}</th>
                  <th style={{ padding: '4px 6px' }}></th>
                </tr>
              </thead>
              <tbody>
                {historyData.cancellations.map((cn) => {
                  const isSystemNoShow = cn.cancelled_by == null;
                  const isToday = historyDate === todayISO;
                  return (
                    <tr key={`hcan-${cn.id}`} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '4px 6px' }}>{cn.id}</td>
                      <td style={{ padding: '4px 6px' }}>{formatDateTimeGR(cn.arrival)}</td>
                      <td style={{ padding: '4px 6px' }}>{`${cn.client_lastname || ''} ${cn.client_firstname || ''}`.trim() || '—'}</td>
                      <td style={{ padding: '4px 6px' }}>{cn.people ?? ''}</td>
                      <td style={{ padding: '4px 6px' }}>
                        {isSystemNoShow ? (
                          <span style={{ color: '#b02a37', fontWeight: 600 }}>{tr('No-show (automatic)')}</span>
                        ) : (
                          <span>{tr('Cancelled by {{name}}', { name: cn.cancelled_by_username || '' })}</span>
                        )}
                      </td>
                      <td style={{ padding: '4px 6px' }}>
                        {isSystemNoShow && isToday && (
                          <button
                            onClick={() => handleUndoNoShow(cn)}
                            disabled={undoingNoShowId === cn.id}
                            style={{
                              padding: '4px 10px', borderRadius: 6, border: '1px solid #b02a37',
                              background: undoingNoShowId === cn.id ? '#f3d2d6' : 'white',
                              color: '#b02a37', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap'
                            }}
                          >
                            {undoingNoShowId === cn.id ? tr('Restoring...') : tr('Undo no-show')}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      {/* Modal for Editing Reservation */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        style={{
          overlay: { backgroundColor: 'rgba(0,0,0,0.35)', zIndex: 1000 },
          content: isMobile ? {
            inset: '0px',
            margin: 'auto',
            padding: '8px 10px',
            border: '0',
            borderRadius: 10,
            height: '80vh',
            maxWidth: '92vw',
            maxHeight: '80vh',
            overflow: 'auto'
          } : {
            width: '810px',
            margin: 'auto',
            overflowX: 'hidden',
            borderRadius: 10,
            height: '80vh'
          }
        }}
      >
        {isModalOpen && selectedReservation && (
          <ReservationForm
            key={`rf-${selectedReservation.id}-${selectedReservation.date}-${selectedReservation.reservationTime}`}
            reservation={selectedReservation}
            assignedTables={assignedTables}
            onClose={() => setIsModalOpen(false)}
            forceBottomSelector
          />
        )}
      </Modal>

      {/* Modal for Assigning Meats */}
      <Modal
        isOpen={isMeatsModalOpen}
        onRequestClose={() => setIsMeatsModalOpen(false)}
        style={{
          overlay: { backgroundColor: 'rgba(0,0,0,0.35)', zIndex: 1000 },
          content: {
            inset: '0px',
            margin: 'auto',
            padding: '8px 10px',
            border: '0',
            borderRadius: 10,
            width: '88vw',
            maxWidth: '88vw',
            height: 'fit-content',
            maxHeight: '80vh',
            overflow: 'auto'
          }
        }}
      >
        {selectedReservation && (
          <AssignMeats reservation={selectedReservation} onCloseModal={() => setIsMeatsModalOpen(false)} />
        )}
      </Modal>

      {/* QR modal */}
      <Modal
        isOpen={isQrModalOpen}
        onRequestClose={() => setIsQrModalOpen(false)}
        style={{
          overlay: {
            backgroundColor: "rgba(0,0,0,0.88)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            zIndex: 3000
          },
          content: {
            inset: "0px",
            margin: "auto",
            padding: 0,
            border: "0",
            borderRadius: 22,
            width: "min(94vw, 720px)",
            height: "fit-content",
            maxHeight: "94vh",
            overflow: "auto",
            background: "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }
        }}
      >
        <div
          style={{
            width: "100%",
            borderRadius: 22,
            background: "rgba(0,0,0,0.55)",
            boxShadow: "0 20px 80px rgba(0,0,0,0.65)",
            padding: 22,
            textAlign: "center",
            color: "white"
          }}
        >
          <div style={{ fontWeight: 800, color: "white", marginBottom: 10 }}>
            {tr("Buzzer")} #{qrForReservation?.id || ""}

            <div style={{ display: "flex", justifyContent: "center", gap: 10, marginBottom: 10 }}>
              <button
                onClick={() => setQrLang("en")}
                style={{
                  border: qrLang === "en" ? "2px solid #34aadc" : "1px solid rgba(255,255,255,0.25)",
                  background: "rgba(255,255,255,0.10)",
                  borderRadius: 10,
                  padding: 6,
                  cursor: "pointer"
                }}
                title="English"
              >
                <img src="/locales/en/flag.png" alt="English" width={22} height={16} style={{ display: "block", borderRadius: 3 }} />
              </button>

              <button
                onClick={() => setQrLang("el")}
                style={{
                  border: qrLang === "el" ? "2px solid #34aadc" : "1px solid rgba(255,255,255,0.25)",
                  background: "rgba(255,255,255,0.10)",
                  borderRadius: 10,
                  padding: 6,
                  cursor: "pointer"
                }}
                title="Ελληνικά"
              >
                <img src="/locales/el/flag.png" alt="Ελληνικά" width={22} height={16} style={{ display: "block", borderRadius: 3 }} />
              </button>
            </div>
          </div>

          {!!qrFinalUrl && (
            <img
              alt="qr"
              style={{
                width: "min(84vw, 520px, calc(94vh - 190px))",
                height: "min(84vw, 520px, calc(94vh - 190px))",
                aspectRatio: "1 / 1",
                objectFit: "contain",
                borderRadius: 14,
                background: "white",
                padding: 10
              }}
              src={`https://api.qrserver.com/v1/create-qr-code/?size=700x700&data=${encodeURIComponent(qrFinalUrl)}`}
            />
          )}

          {!!qrFinalUrl && (
            <div style={{ marginTop: 10, opacity: 0.8, color: "white", fontSize: 12, wordBreak: "break-all" }}>
              {qrFinalUrl}
            </div>
          )}

          <button
            onClick={() => setIsQrModalOpen(false)}
            style={{
              marginTop: 12,
              padding: "10px 16px",
              borderRadius: 12,
              border: 0,
              cursor: "pointer",
              fontWeight: 800
            }}
          >
            {tr("Close")}
          </button>
        </div>
      </Modal>

      {/* Feedback modal */}
      <Modal
        isOpen={isFbModalOpen}
        onRequestClose={() => setIsFbModalOpen(false)}
        style={{
          overlay: {
            backgroundColor: "rgba(0,0,0,0.55)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            zIndex: 4000
          },
          content: {
            inset: "0px",
            margin: "auto",
            padding: 0,
            border: "0",
            borderRadius: 22,
            width: "min(94vw, 720px)",
            maxHeight: "94vh",
            overflow: "hidden",
            background: "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }
        }}
      >
        {fbReservation && (
          <div className="fbModal">
            <div className="fbHead">
              <div>
                <div className="fbTitle">{tr("Feedback")}</div>

                {(() => {
                  const fullName = `${fbReservation.client_lastname || ""} ${fbReservation.client_firstname || ""}`.trim();
                  return fullName ? <div className="fbSubTitle">{fullName}</div> : null;
                })()}
              </div>

              <div className="fbIdPill">#{fbReservation.id}</div>
            </div>

            <div className="fbBody">
              <div className="fbRow">
                <div className="fbLabel">{tr("Managment & Service")}</div>
                <div className="fbStars">{renderStars(Number(fbReservation.feedback_org_service || 0))}</div>
              </div>

              <div className="fbRow">
                <div className="fbLabel">{tr("Quality / Cost")}</div>
                <div className="fbStars">{renderStars(Number(fbReservation.feedback_quality_cost || 0))}</div>
              </div>

              <div className="fbRow">
                <div className="fbLabel">{tr("Environment & Cleanliness")}</div>
                <div className="fbStars">{renderStars(Number(fbReservation.feedback_environment_clean || 0))}</div>
              </div>

              {!!fbReservation.feedback_comment && (
                <div className="fbComment">
                  {fbReservation.feedback_comment}
                </div>
              )}
            </div>

            <div className="fbFoot">
              <button className="fbBtn" onClick={() => setIsFbModalOpen(false)}>
                {tr("Close")}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Mini availability popover */}
      {miniHover && miniLayout && miniHoverTablesOverride
        ? createPortal(
            <div
              className="rlMiniAvailPopover"
              style={{
                left: miniLayout.left,
                top: miniLayout.top,
                width: miniLayout.outerW,
                height: miniLayout.outerH,
              }}
              onMouseEnter={cancelCloseMiniHover}
              onMouseLeave={scheduleCloseMiniHover}
            >
              <button
                type="button"
                className="rlMiniAvailClose"
                onClick={() => setMiniHover(null)}
                aria-label={tr("Close")}
              >
                ×
              </button>

              <div className="rlMiniAvailHead" ref={miniHeadRef}>
                <div className="rlMiniAvailTitle">
                  {miniHoverHeaderNode || (<><span className="rlMiniAvailStrong">{miniHover.dateKey}</span> • <span className="rlMiniAvailStrong">{miniHover.label}</span></>)}
                </div>
              </div>

              <div className="rlMiniAvailViewport">
                <div className="rlMiniAvailScale" ref={miniScaleRef} style={{ transform: `scale(${miniLayout.scale})`, display: "inline-block", width: "max-content" }}>
                  <TableSelector
                    reservationTime={`${miniHover.dateKey}T${miniHover.label}`}
                    selectedAssignedTables={[]}
                    onTablesSelected={() => {}}
                    people={0}
                    onTableClick={() => {}}
                    tablesOverride={miniHoverTablesOverride}
                  />
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

    </div>
  );
};

export default ReservationsList;
